//src/pages/LegalPage.js
// src/pages/LegalPage.js
import React, { useContext, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";

import TopBanner from "../components/TopBanner";
import MainHeader from "../components/MainHeader";
import CartDrawer from "../components/CartDrawer";
import AddedToBagToast from "../components/AddedToBagToast";
import Footer from "../components/Footer";
import { LanguageContext } from "../context/LanguageContext";

const LAST_UPDATED_EN = "June 22, 2026";
const LAST_UPDATED_AR = "22 حزيران 2026";

const PRIVACY_CONTENT = {
  EN: {
    title: "Privacy Policy",
    intro:
      "This Privacy Policy explains how ESIELL collects, uses, stores, shares, and protects personal information when you visit our website, create an account, place an order, communicate with us, or interact with our services.",
    sections: [
      {
        title: "1. Who We Are",
        paragraphs: [
          "ESIELL is a footwear and fashion retailer operating in Lebanon. In this Privacy Policy, “ESIELL”, “we”, “us”, and “our” refer to ESIELL and the people responsible for operating esiell.com and its related customer services.",
          "This Privacy Policy applies to esiell.com, our customer accounts, order services, customer support channels, marketing communications, analytics tools, and WhatsApp customer and review communications.",
        ],
      },
      {
        title: "2. Information We Collect",
        paragraphs: [
          "We may collect information that you provide directly to us, information generated when you use our website, and limited information received from service providers that help us operate our business.",
        ],
        bullets: [
          "Identity and account information, such as your name, email address, phone number, password credentials in protected form, and account preferences.",
          "Order and delivery information, such as products purchased, selected sizes and colors, delivery address, order notes, order status, and transaction details.",
          "Customer-support information, such as messages, questions, complaints, return requests, and communications sent through our website, email, social media, or WhatsApp.",
          "Review information, such as your written review, star rating, image, video, voice note, media caption, and related product or order information when you participate in our review process.",
          "Marketing information, such as your email address and communication preferences when you join our mailing list or agree to receive promotional messages.",
          "Technical and usage information, such as IP address, browser type, device type, operating system, pages viewed, session activity, referral source, timestamps, cookie identifiers, and website interaction events.",
          "Shopping information, such as your cart, wishlist, viewed products, search activity, abandoned-cart activity, and purchase events.",
          "Fraud-prevention and security information needed to protect accounts, transactions, our website, and our customers.",
        ],
      },
      {
        title: "3. How We Use Your Information",
        bullets: [
          "To create and manage your customer account.",
          "To process, confirm, prepare, deliver, update, and support your orders.",
          "To communicate with you about orders, delivery, returns, account activity, and customer-support requests.",
          "To send review invitations after an eligible order and process reviews that you voluntarily submit.",
          "To display approved customer reviews on our website or related ESIELL marketing channels.",
          "To operate your cart, wishlist, checkout, saved preferences, and other website features.",
          "To provide requested marketing communications and promotional offers.",
          "To measure website usage, sales performance, advertising effectiveness, and customer experience.",
          "To prevent fraud, abuse, security incidents, and unauthorized activity.",
          "To maintain records, enforce our Terms of Service, resolve disputes, and comply with legal obligations.",
          "To improve our products, website, customer support, marketing, and business operations.",
        ],
      },
      {
        title: "4. Orders and Payments",
        paragraphs: [
          "When you place an order, we use the information you provide to process the order, communicate with you, arrange delivery or pickup, prevent fraud, and maintain transaction records.",
          "Payment information may be processed by the applicable payment provider. ESIELL does not intentionally store complete payment-card numbers or payment security codes unless a payment method and applicable law specifically require and permit it.",
          "Payment providers may process your information under their own privacy policies and terms.",
        ],
      },
      {
        title: "5. WhatsApp Communications and Customer Reviews",
        paragraphs: [
          "If you provide a phone number, place an eligible order, contact us through WhatsApp, or otherwise agree to communicate with us through WhatsApp, we may use WhatsApp to send customer-service, order-related, delivery, and review-request messages.",
          "A review request may identify the purchased product and invite you to respond by text, star rating, image, video, voice note, or media caption. Participation is voluntary.",
          "Messages and media submitted through the review process may be reviewed by authorized ESIELL administrators. We may contact you for clarification or moderation before publishing a review.",
          "We do not intentionally publish your phone number, delivery address, email address, or other private order details as part of a customer review.",
          "Approved reviews may be displayed with a limited customer identifier, such as your first name, initials, or another display name associated with the review.",
          "WhatsApp and Meta may process information according to their own terms, privacy policies, and business-service arrangements.",
        ],
      },
      {
        title: "6. Meta Technologies, Advertising, and Analytics",
        paragraphs: [
          "Our website may use Meta technologies, including the Meta Pixel and Conversions API, to measure website activity, understand advertising performance, improve campaign delivery, and create relevant advertising audiences where permitted.",
          "Information shared with Meta may include browser and device information, IP address, cookie identifiers, page views, product views, cart activity, checkout activity, purchases, event timestamps, and hashed contact information when available and permitted.",
          "We may use matching, event identifiers, and deduplication methods to improve measurement accuracy and avoid counting the same event more than once.",
          "Meta processes information under its own privacy terms and applicable data-processing terms. You may manage certain advertising preferences through your Meta account and browser settings.",
        ],
      },
      {
        title: "7. Cookies and Similar Technologies",
        paragraphs: [
          "We may use cookies, local storage, pixels, tags, and similar technologies to keep the website functioning, remember preferences, maintain carts and sessions, analyze website usage, prevent fraud, and measure advertising.",
        ],
        bullets: [
          "Essential technologies required for login, carts, checkout, security, language preferences, and core website functions.",
          "Analytics technologies used to understand traffic, performance, and customer interaction.",
          "Advertising technologies used to measure campaigns and provide more relevant advertising.",
          "Preference technologies used to remember choices and improve your experience.",
        ],
      },
      {
        title: "8. Marketing Communications",
        paragraphs: [
          "We may send marketing emails or messages when you subscribe, consent, or when otherwise permitted by applicable law.",
          "You may unsubscribe using an available unsubscribe option or contact us and ask to stop receiving promotional communications.",
          "Even after you opt out of marketing, we may continue sending necessary non-promotional messages about your account, orders, delivery, security, or customer-support requests.",
        ],
      },
      {
        title: "9. How We Share Information",
        paragraphs: [
          "We do not sell your personal information as a standalone product.",
          "We may share the minimum information reasonably necessary with trusted parties that help us operate our business.",
        ],
        bullets: [
          "Hosting, database, cloud-storage, security, and technical-service providers.",
          "Delivery companies and couriers that need order and contact details to complete delivery.",
          "Payment processors and financial-service providers involved in a transaction.",
          "Email, messaging, WhatsApp, customer-support, and communication providers.",
          "Analytics and advertising providers, including Meta, where applicable.",
          "Professional advisers, such as accountants, auditors, insurers, or legal advisers.",
          "Government authorities or other parties when disclosure is required by law, a valid legal request, or necessary to protect rights, safety, and security.",
          "A purchaser, successor, or related party involved in a legitimate merger, acquisition, restructuring, financing, or transfer of the business, subject to appropriate safeguards.",
        ],
      },
      {
        title: "10. International Data Processing",
        paragraphs: [
          "Some service providers may process or store information outside Lebanon. Privacy and data-protection rules in those locations may differ from Lebanese rules.",
          "Where reasonably required, we use contractual, organizational, and technical measures intended to protect information handled by service providers.",
        ],
      },
      {
        title: "11. Data Retention",
        paragraphs: [
          "We retain personal information only for as long as reasonably necessary for the purpose for which it was collected, including order fulfillment, customer support, accounting, fraud prevention, dispute resolution, legal compliance, security, and enforcement of our agreements.",
          "Retention periods may vary according to the type of information. Some order and transaction records may need to be kept for legal, accounting, tax, or evidentiary purposes.",
          "When information is no longer reasonably needed, we may delete it, anonymize it, or securely isolate it until deletion is possible.",
        ],
      },
      {
        title: "12. Security",
        paragraphs: [
          "We use reasonable administrative, technical, and organizational safeguards intended to protect personal information against unauthorized access, loss, misuse, alteration, or disclosure.",
          "No website, database, internet transmission, or storage system can be guaranteed to be completely secure. You are responsible for protecting your account password and informing us if you believe your account or information has been compromised.",
        ],
      },
      {
        title: "13. Your Choices and Rights",
        paragraphs: [
          "Subject to applicable law and reasonable identity verification, you may ask us to:",
        ],
        bullets: [
          "Confirm whether we hold personal information about you.",
          "Provide access to certain personal information.",
          "Correct inaccurate or incomplete information.",
          "Delete eligible personal information.",
          "Object to or restrict certain processing.",
          "Withdraw consent where processing depends on consent.",
          "Stop promotional communications.",
          "Clarify how your information is used or shared.",
        ],
      },
      {
        title: "14. Account and Data Deletion Requests",
        paragraphs: [
          "You may request deletion of your ESIELL account and eligible associated personal information through our Contact page or by sending a WhatsApp message to our customer-service number.",
          "Please provide enough information for us to identify your account, such as your name, account email address, and phone number. We may request reasonable verification before completing a deletion request.",
          "After verification, we will delete or anonymize eligible information within a reasonable period. We may retain information that is necessary for legal obligations, completed transactions, fraud prevention, security, dispute resolution, or enforcement of agreements.",
          "Deleting an ESIELL account does not necessarily delete information independently controlled by third-party providers such as Meta, WhatsApp, payment providers, or delivery companies. Requests concerning those services may need to be submitted directly to the relevant provider.",
        ],
      },
      {
        title: "15. Customer Reviews and Published Content",
        paragraphs: [
          "You may ask us to correct or remove a customer review you submitted. Removal from our website does not necessarily erase records that must be retained for security, moderation, dispute resolution, or legal purposes.",
          "Before submitting a review, avoid including phone numbers, addresses, payment information, private conversations, or personal information about another person.",
        ],
      },
      {
        title: "16. Children’s Privacy",
        paragraphs: [
          "Our website and services are not directed to children who are unable to enter into a valid transaction under applicable law.",
          "We do not knowingly collect personal information from a child without appropriate authorization. A parent or legal guardian who believes that a child has provided personal information may contact us to request review and deletion.",
        ],
      },
      {
        title: "17. Third-Party Links",
        paragraphs: [
          "Our website may contain links to social-media platforms, maps, payment services, delivery services, or other third-party websites.",
          "We are not responsible for the privacy practices, security, availability, or content of third-party services. Please review their policies before providing information.",
        ],
      },
      {
        title: "18. Changes to This Privacy Policy",
        paragraphs: [
          "We may update this Privacy Policy when our services, technology, providers, or legal obligations change.",
          "The latest version will be published on this page with an updated effective date. Material changes may also be communicated through the website or another appropriate channel.",
        ],
      },
      {
        title: "19. Contact Us",
        paragraphs: [
          "For privacy questions, access requests, corrections, account deletion, review removal, or other data-related requests, contact ESIELL through our website Contact page or WhatsApp customer service.",
        ],
        links: [
          {
            label: "Open the Contact page",
            to: "/contact",
          },
          {
            label: "Contact ESIELL on WhatsApp",
            href: "https://wa.me/96179338100",
          },
        ],
      },
    ],
  },

  AR: {
    title: "سياسة الخصوصية",
    intro:
      "توضح سياسة الخصوصية هذه كيفية قيام إيسيل بجمع المعلومات الشخصية واستخدامها وحفظها ومشاركتها وحمايتها عند زيارة موقعنا الإلكتروني أو إنشاء حساب أو تقديم طلب شراء أو التواصل معنا أو استخدام خدماتنا.",
    sections: [
      {
        title: "1. من نحن",
        paragraphs: [
          "إيسيل هي جهة متخصصة في بيع الأحذية ومنتجات الموضة في لبنان. ويُقصد بعبارات «إيسيل» و«نحن» و«لنا» في هذه السياسة، إيسيل والأشخاص المسؤولون عن تشغيل موقع esiell.com وخدمات الزبائن المرتبطة به.",
          "تنطبق هذه السياسة على موقع esiell.com وحسابات الزبائن والطلبات وخدمات الدعم والتواصل التسويقي وأدوات التحليل ومراسلات واتساب وخدمة مراجعات العملاء.",
        ],
      },
      {
        title: "2. المعلومات التي نجمعها",
        paragraphs: [
          "قد نجمع المعلومات التي تزودنا بها مباشرة، والمعلومات الناتجة عن استخدامك للموقع، وبعض المعلومات التي نحصل عليها من مزودي الخدمات الذين يساعدوننا في تشغيل أعمالنا.",
        ],
        bullets: [
          "معلومات الهوية والحساب، مثل الاسم والبريد الإلكتروني ورقم الهاتف وبيانات كلمة المرور المحفوظة بصيغة محمية وتفضيلات الحساب.",
          "معلومات الطلب والتوصيل، مثل المنتجات والمقاسات والألوان المختارة وعنوان التوصيل وملاحظات الطلب وحالته وتفاصيل المعاملة.",
          "معلومات خدمة الزبائن، مثل الرسائل والأسئلة والشكاوى وطلبات الإرجاع والمراسلات عبر الموقع أو البريد الإلكتروني أو وسائل التواصل الاجتماعي أو واتساب.",
          "معلومات المراجعات، مثل نص المراجعة والتقييم بالنجوم والصور والفيديوهات والملاحظات الصوتية والتعليقات المرفقة بالوسائط ومعلومات المنتج أو الطلب المرتبط بها.",
          "معلومات التسويق، مثل بريدك الإلكتروني وتفضيلات التواصل عند الاشتراك في قائمتنا البريدية أو الموافقة على الرسائل الترويجية.",
          "المعلومات التقنية ومعلومات الاستخدام، مثل عنوان IP ونوع المتصفح والجهاز ونظام التشغيل والصفحات التي تمت زيارتها ونشاط الجلسة ومصدر الزيارة والتوقيتات ومعرّفات ملفات تعريف الارتباط.",
          "معلومات التسوق، مثل سلة التسوق وقائمة الرغبات والمنتجات التي تمت مشاهدتها وعمليات البحث والسلات المتروكة وأحداث الشراء.",
          "المعلومات اللازمة لمنع الاحتيال وحماية الحسابات والمعاملات والموقع والزبائن.",
        ],
      },
      {
        title: "3. كيفية استخدام معلوماتك",
        bullets: [
          "إنشاء حساب الزبون وإدارته.",
          "معالجة طلباتك وتأكيدها وتجهيزها وتوصيلها وتحديثها ودعمها.",
          "التواصل معك بشأن الطلبات والتوصيل والإرجاع ونشاط الحساب وطلبات الدعم.",
          "إرسال طلبات المراجعة بعد الطلبات المؤهلة ومعالجة المراجعات التي ترسلينها بمحض إرادتك.",
          "عرض المراجعات المعتمدة على الموقع أو قنوات إيسيل التسويقية.",
          "تشغيل سلة التسوق وقائمة الرغبات والدفع والتفضيلات المحفوظة وميزات الموقع.",
          "إرسال الرسائل التسويقية والعروض التي طلبتها أو وافقت عليها.",
          "قياس استخدام الموقع والمبيعات وفعالية الإعلانات وتجربة الزبائن.",
          "منع الاحتيال وإساءة الاستخدام والحوادث الأمنية والنشاط غير المصرح به.",
          "حفظ السجلات وتطبيق شروط الخدمة وحل النزاعات والامتثال للالتزامات القانونية.",
          "تحسين المنتجات والموقع وخدمة الزبائن والتسويق والعمليات التجارية.",
        ],
      },
      {
        title: "4. الطلبات وعمليات الدفع",
        paragraphs: [
          "عند تقديم طلب، نستخدم المعلومات التي تقدمينها لمعالجة الطلب والتواصل معك وترتيب التوصيل أو الاستلام ومنع الاحتيال وحفظ سجلات المعاملة.",
          "قد تتم معالجة معلومات الدفع بواسطة مزود الدفع المعتمد. لا تقوم إيسيل عن قصد بحفظ أرقام البطاقات الكاملة أو رموز الحماية إلا إذا كانت وسيلة الدفع والقانون المطبق يسمحان بذلك ويتطلبانه.",
          "قد يعالج مزودو الدفع معلوماتك وفقاً لسياسات الخصوصية والشروط الخاصة بهم.",
        ],
      },
      {
        title: "5. مراسلات واتساب ومراجعات العملاء",
        paragraphs: [
          "إذا قدمت رقم هاتف، أو نفذت طلباً مؤهلاً، أو تواصلت معنا عبر واتساب، أو وافقت على التواصل عبره، فقد نستخدم واتساب لإرسال رسائل خدمة الزبائن والطلبات والتوصيل وطلبات المراجعة.",
          "قد يتضمن طلب المراجعة المنتج الذي تم شراؤه، وقد يطلب منك الرد بنص أو تقييم بالنجوم أو صورة أو فيديو أو ملاحظة صوتية أو تعليق مرفق بالوسائط. المشاركة اختيارية.",
          "قد يراجع موظفو إيسيل المخولون الرسائل والوسائط المقدمة ضمن عملية المراجعة، وقد نتواصل معك للتوضيح أو المراجعة قبل النشر.",
          "لا ننشر عن قصد رقم هاتفك أو عنوان التوصيل أو بريدك الإلكتروني أو بيانات الطلب الخاصة ضمن مراجعة العميل.",
          "قد تظهر المراجعات المعتمدة مع تعريف محدود، مثل الاسم الأول أو الأحرف الأولى أو اسم عرض مرتبط بالمراجعة.",
          "قد تقوم واتساب وMeta بمعالجة المعلومات وفقاً للشروط وسياسات الخصوصية وترتيبات خدمات الأعمال الخاصة بهما.",
        ],
      },
      {
        title: "6. تقنيات Meta والإعلانات والتحليلات",
        paragraphs: [
          "قد يستخدم موقعنا تقنيات Meta، بما فيها Meta Pixel وConversions API، لقياس نشاط الموقع وفهم أداء الإعلانات وتحسين الحملات وإنشاء جماهير إعلانية مناسبة حيثما يكون ذلك مسموحاً.",
          "قد تشمل المعلومات التي تتم مشاركتها مع Meta معلومات المتصفح والجهاز وعنوان IP ومعرّفات ملفات تعريف الارتباط ومشاهدات الصفحات والمنتجات ونشاط السلة والدفع والشراء وتوقيت الأحداث ومعلومات اتصال مشفرة عند توفرها والسماح باستخدامها.",
          "قد نستخدم معرّفات الأحداث وتقنيات المطابقة ومنع التكرار لتحسين دقة القياس وتجنب احتساب الحدث نفسه أكثر من مرة.",
          "تعالج Meta المعلومات وفقاً لشروط الخصوصية وشروط معالجة البيانات الخاصة بها. ويمكنك إدارة بعض تفضيلات الإعلانات من حساب Meta وإعدادات المتصفح.",
        ],
      },
      {
        title: "7. ملفات تعريف الارتباط والتقنيات المشابهة",
        paragraphs: [
          "قد نستخدم ملفات تعريف الارتباط والتخزين المحلي والبكسلات والعلامات والتقنيات المشابهة لتشغيل الموقع وتذكر التفضيلات والمحافظة على السلة والجلسة وتحليل الاستخدام ومنع الاحتيال وقياس الإعلانات.",
        ],
        bullets: [
          "تقنيات ضرورية لتسجيل الدخول والسلة والدفع والأمان واللغة ووظائف الموقع الأساسية.",
          "تقنيات تحليل لفهم الزيارات والأداء وتفاعل الزبائن.",
          "تقنيات إعلانية لقياس الحملات وتقديم إعلانات أكثر صلة.",
          "تقنيات تفضيلات لتذكر اختياراتك وتحسين تجربتك.",
        ],
      },
      {
        title: "8. الرسائل التسويقية",
        paragraphs: [
          "قد نرسل رسائل تسويقية عبر البريد الإلكتروني أو وسائل أخرى عندما تشترك أو توافق أو عندما يسمح القانون بذلك.",
          "يمكنك إلغاء الاشتراك باستخدام خيار إلغاء الاشتراك المتوفر أو التواصل معنا لطلب إيقاف الرسائل الترويجية.",
          "حتى بعد إلغاء الرسائل التسويقية، قد نستمر بإرسال الرسائل الضرورية وغير الترويجية المتعلقة بالحساب أو الطلب أو التوصيل أو الأمان أو خدمة الزبائن.",
        ],
      },
      {
        title: "9. كيفية مشاركة المعلومات",
        paragraphs: [
          "نحن لا نبيع معلوماتك الشخصية كمنتج مستقل.",
          "قد نشارك الحد الأدنى اللازم من المعلومات مع جهات موثوقة تساعدنا في تشغيل أعمالنا.",
        ],
        bullets: [
          "مزودو الاستضافة وقواعد البيانات والتخزين السحابي والأمان والخدمات التقنية.",
          "شركات التوصيل التي تحتاج إلى تفاصيل الطلب والتواصل لإتمام التوصيل.",
          "مزودو الدفع والخدمات المالية المشاركون في المعاملة.",
          "مزودو البريد الإلكتروني والمراسلة وواتساب وخدمة الزبائن.",
          "مزودو التحليلات والإعلانات، بما في ذلك Meta عند الاقتضاء.",
          "المستشارون المهنيون، مثل المحاسبين والمدققين وشركات التأمين والمستشارين القانونيين.",
          "الجهات الحكومية أو غيرها عندما يكون الإفصاح مطلوباً قانوناً أو ضرورياً لحماية الحقوق أو السلامة أو الأمان.",
          "أي مشترٍ أو خلف قانوني أو طرف مرتبط باندماج أو استحواذ أو إعادة هيكلة أو تمويل أو نقل مشروع بصورة مشروعة، مع اتخاذ الضمانات المناسبة.",
        ],
      },
      {
        title: "10. معالجة البيانات خارج لبنان",
        paragraphs: [
          "قد يقوم بعض مزودي الخدمات بمعالجة المعلومات أو حفظها خارج لبنان، وقد تختلف قوانين الخصوصية في تلك الدول عن القوانين اللبنانية.",
          "عند الحاجة، نستخدم إجراءات تعاقدية وتنظيمية وتقنية تهدف إلى حماية المعلومات التي يعالجها مزودو الخدمات.",
        ],
      },
      {
        title: "11. مدة الاحتفاظ بالمعلومات",
        paragraphs: [
          "نحتفظ بالمعلومات الشخصية فقط للمدة اللازمة بشكل معقول للغرض الذي جُمعت من أجله، بما يشمل تنفيذ الطلبات وخدمة الزبائن والمحاسبة ومنع الاحتيال وحل النزاعات والامتثال القانوني والأمان وتطبيق الاتفاقيات.",
          "تختلف مدة الاحتفاظ بحسب نوع المعلومات، وقد يلزم حفظ بعض سجلات الطلبات والمعاملات لأغراض قانونية أو محاسبية أو ضريبية أو إثباتية.",
          "عندما لا تعود المعلومات مطلوبة، قد نحذفها أو نخفي هوية صاحبها أو نعزلها بشكل آمن إلى حين إمكانية حذفها.",
        ],
      },
      {
        title: "12. حماية المعلومات",
        paragraphs: [
          "نستخدم إجراءات إدارية وتقنية وتنظيمية معقولة تهدف إلى حماية المعلومات من الوصول غير المصرح به أو الفقدان أو إساءة الاستخدام أو التعديل أو الإفصاح.",
          "لا يمكن ضمان أمان أي موقع أو قاعدة بيانات أو إرسال عبر الإنترنت بنسبة كاملة. وتقع عليك مسؤولية حماية كلمة مرور حسابك وإبلاغنا إذا اعتقدت أن حسابك أو معلوماتك تعرضت للاختراق.",
        ],
      },
      {
        title: "13. خياراتك وحقوقك",
        paragraphs: [
          "وفقاً للقانون المطبق وبعد التحقق المعقول من الهوية، يمكنك طلب ما يلي:",
        ],
        bullets: [
          "معرفة ما إذا كنا نحتفظ بمعلومات شخصية تخصك.",
          "الوصول إلى بعض معلوماتك الشخصية.",
          "تصحيح المعلومات غير الدقيقة أو غير المكتملة.",
          "حذف المعلومات المؤهلة للحذف.",
          "الاعتراض على بعض أنواع المعالجة أو طلب تقييدها.",
          "سحب الموافقة عندما تعتمد المعالجة على موافقتك.",
          "إيقاف الرسائل الترويجية.",
          "طلب توضيح حول كيفية استخدام معلوماتك أو مشاركتها.",
        ],
      },
      {
        title: "14. طلب حذف الحساب والبيانات",
        paragraphs: [
          "يمكنك طلب حذف حساب إيسيل والمعلومات الشخصية المؤهلة والمرتبطة به من خلال صفحة «تواصل معنا» أو بإرسال رسالة إلى رقم خدمة الزبائن عبر واتساب.",
          "يرجى تزويدنا بمعلومات كافية لتحديد حسابك، مثل الاسم وبريد الحساب ورقم الهاتف. وقد نطلب التحقق بصورة معقولة من الهوية قبل تنفيذ الطلب.",
          "بعد التحقق، سنحذف المعلومات المؤهلة أو نخفي هوية صاحبها خلال مدة معقولة. وقد نحتفظ بالمعلومات الضرورية للالتزامات القانونية أو المعاملات المكتملة أو منع الاحتيال أو الأمان أو حل النزاعات أو تطبيق الاتفاقيات.",
          "لا يؤدي حذف حساب إيسيل بالضرورة إلى حذف المعلومات التي تتحكم بها جهات مستقلة، مثل Meta أو واتساب أو مزودي الدفع أو شركات التوصيل. وقد يلزم إرسال الطلب مباشرة إلى الجهة المعنية.",
        ],
      },
      {
        title: "15. المراجعات والمحتوى المنشور",
        paragraphs: [
          "يمكنك أن تطلب منا تصحيح أو إزالة مراجعة سبق أن قدمتها. ولا يعني حذفها من الموقع بالضرورة حذف السجلات التي يلزم الاحتفاظ بها لأغراض الأمان أو الإشراف أو حل النزاعات أو الالتزام القانوني.",
          "يجب عدم تضمين أرقام الهواتف أو العناوين أو معلومات الدفع أو المحادثات الخاصة أو معلومات شخصية تخص شخصاً آخر داخل المراجعة.",
        ],
      },
      {
        title: "16. خصوصية الأطفال",
        paragraphs: [
          "موقعنا وخدماتنا ليست موجهة للأطفال الذين لا يستطيعون إبرام معاملة صحيحة وفقاً للقانون.",
          "لا نجمع عن قصد معلومات شخصية من طفل دون التفويض المناسب. ويمكن لولي الأمر الذي يعتقد أن طفلاً قدم معلومات شخصية التواصل معنا لطلب مراجعتها وحذفها.",
        ],
      },
      {
        title: "17. روابط الجهات الأخرى",
        paragraphs: [
          "قد يحتوي الموقع على روابط إلى وسائل التواصل الاجتماعي أو الخرائط أو خدمات الدفع أو التوصيل أو مواقع جهات أخرى.",
          "نحن غير مسؤولين عن ممارسات الخصوصية أو الأمان أو التوفر أو محتوى الخدمات الخارجية. يرجى مراجعة سياساتها قبل تقديم معلوماتك.",
        ],
      },
      {
        title: "18. تحديث سياسة الخصوصية",
        paragraphs: [
          "قد نقوم بتحديث هذه السياسة عندما تتغير خدماتنا أو تقنياتنا أو مزودو خدماتنا أو التزاماتنا القانونية.",
          "سيتم نشر أحدث نسخة في هذه الصفحة مع تاريخ التحديث. وقد يتم الإعلان عن التغييرات المهمة عبر الموقع أو وسيلة مناسبة أخرى.",
        ],
      },
      {
        title: "19. التواصل معنا",
        paragraphs: [
          "للاستفسارات المتعلقة بالخصوصية أو الوصول إلى المعلومات أو تصحيحها أو حذف الحساب أو إزالة مراجعة أو أي طلب متعلق بالبيانات، تواصلي مع إيسيل عبر صفحة التواصل أو واتساب.",
        ],
        links: [
          {
            label: "فتح صفحة التواصل",
            to: "/contact",
          },
          {
            label: "التواصل مع إيسيل عبر واتساب",
            href: "https://wa.me/96179338100",
          },
        ],
      },
    ],
  },
};

const TERMS_CONTENT = {
  EN: {
    title: "Terms of Service",
    intro:
      "These Terms of Service govern your access to and use of esiell.com and your purchase of products or use of services provided by ESIELL. By using the website or placing an order, you agree to these Terms.",
    sections: [
      {
        title: "1. About ESIELL",
        paragraphs: [
          "ESIELL is a footwear and fashion retailer operating in Lebanon. These Terms apply to the ESIELL website, customer accounts, orders, customer support, reviews, promotions, and related services.",
          "References to “ESIELL”, “we”, “us”, and “our” mean ESIELL and the people responsible for operating the website and providing its services.",
        ],
      },
      {
        title: "2. Acceptance of These Terms",
        paragraphs: [
          "By accessing the website, creating an account, placing an order, submitting content, or using any ESIELL service, you confirm that you have read and accepted these Terms and our Privacy Policy.",
          "If you do not agree, you should not use the website or submit an order.",
        ],
      },
      {
        title: "3. Eligibility",
        paragraphs: [
          "You must have the legal capacity to enter into a purchase transaction under applicable law.",
          "A person using the website on behalf of another individual, company, or organization confirms that they have authority to act on that party’s behalf.",
        ],
      },
      {
        title: "4. Customer Accounts",
        bullets: [
          "You must provide accurate, current, and complete information.",
          "You are responsible for keeping your password and login details confidential.",
          "You are responsible for activity conducted through your account unless you promptly report unauthorized access.",
          "You must not impersonate another person, create fraudulent accounts, or use the website for unlawful purposes.",
          "We may suspend or restrict an account when reasonably necessary to protect customers, ESIELL, or the website.",
        ],
      },
      {
        title: "5. Product Information",
        paragraphs: [
          "We aim to present product names, descriptions, colors, materials, images, sizes, prices, and availability as accurately as reasonably possible.",
          "Colors and visual details may appear differently depending on lighting, photography, screen settings, and the device used.",
          "Minor variations in packaging, finishing, texture, or appearance may occur and do not necessarily mean that the product is defective.",
          "Product availability is not guaranteed until the order is reviewed and confirmed.",
        ],
      },
      {
        title: "6. Sizing",
        paragraphs: [
          "Customers are responsible for selecting their preferred size. Size guidance is provided for assistance but cannot guarantee an identical fit for every person or design.",
          "You may contact ESIELL before ordering if you need help selecting a size.",
        ],
      },
      {
        title: "7. Prices",
        paragraphs: [
          "Prices are displayed in the currency shown on the website and may change without prior notice.",
          "The price applicable to an order is generally the price displayed when the order is submitted, subject to correction of obvious errors.",
          "Delivery charges, additional fees, discounts, and promotional adjustments may be shown separately during checkout or order confirmation.",
          "If a price is clearly incorrect because of a technical, administrative, or typographical error, we may contact you to correct or cancel the affected order.",
        ],
      },
      {
        title: "8. Placing an Order",
        paragraphs: [
          "Submitting an order is an offer to purchase the selected products. An automated confirmation or on-screen message confirms receipt of the request but does not necessarily mean final acceptance.",
          "An order becomes accepted when ESIELL confirms it, begins preparing it, dispatches it, or otherwise communicates acceptance.",
          "We may contact you to verify the order, address, telephone number, availability, payment method, or other information.",
        ],
      },
      {
        title: "9. Order Refusal or Cancellation",
        paragraphs: [
          "We may refuse, limit, hold, or cancel an order for reasonable reasons, including product unavailability, incorrect pricing, suspected fraud, incomplete information, failed verification, delivery limitations, abusive activity, or a technical error.",
          "When reasonably possible, we will notify you using the contact information provided with the order.",
          "If we cancel an order after receiving a payment, any eligible refund will be handled through an appropriate method, subject to payment-provider processing times.",
        ],
      },
      {
        title: "10. Payments",
        paragraphs: [
          "Available payment methods may vary by order, location, delivery method, or website configuration.",
          "You confirm that you are authorized to use the payment method supplied for an order.",
          "Payments processed by an external provider may also be governed by that provider’s terms and policies.",
          "ESIELL may require payment confirmation or additional verification before accepting or dispatching an order.",
        ],
      },
      {
        title: "11. Delivery and Pickup",
        paragraphs: [
          "Delivery estimates are provided in good faith but are not guaranteed unless expressly stated otherwise.",
          "Delivery may be affected by courier operations, weather, traffic, address accuracy, availability, public holidays, security conditions, force majeure, or events outside our reasonable control.",
          "You are responsible for providing a complete and accurate address and a reachable telephone number.",
          "You or an authorized recipient may be required to inspect and accept the order at delivery or pickup.",
          "Additional delivery attempts or address corrections may result in an additional charge where reasonably applicable.",
        ],
      },
      {
        title: "12. Inspection, Exchanges, Returns, and Refunds",
        paragraphs: [
          "You should inspect products promptly after receiving them and contact us as soon as reasonably possible if an item is missing, damaged, defective, or materially different from the confirmed order.",
          "Exchange, return, and refund eligibility may depend on the item’s condition, the reason for the request, hygiene considerations, usage, packaging, timing, availability, and applicable law.",
          "Products should be kept unused, unworn, undamaged, clean, and in their original condition and packaging while an exchange or return request is being reviewed.",
          "Do not return a product without first contacting ESIELL and receiving instructions.",
          "Any delivery, exchange, or return charge will be communicated according to the circumstances of the request.",
          "Nothing in these Terms excludes any mandatory customer right that cannot legally be excluded.",
        ],
      },
      {
        title: "13. Promotions and Discount Codes",
        paragraphs: [
          "Promotions, discount codes, free-delivery offers, gifts, and sale prices may be subject to specific eligibility conditions, dates, stock limitations, product exclusions, minimum values, geographic limits, and usage restrictions.",
          "Unless expressly stated otherwise, promotional offers cannot be combined, exchanged for cash, transferred, or applied retroactively.",
          "We may reject or cancel the misuse of a promotion, including duplicated, automated, fraudulent, or unauthorized use.",
        ],
      },
      {
        title: "14. Customer Communications",
        paragraphs: [
          "You agree that we may contact you using the email address, telephone number, website messaging, or WhatsApp number connected to your account or order for operational communications.",
          "Operational communications may include order confirmation, verification, availability, delivery, payment, returns, support, security, and review invitations.",
          "Marketing communications are handled according to the Privacy Policy and applicable consent or unsubscribe choices.",
        ],
      },
      {
        title: "15. Customer Reviews",
        paragraphs: [
          "Customers may be invited to submit reviews by text, rating, image, video, voice note, or other supported media.",
          "By voluntarily submitting a review, you confirm that the content is based on your genuine experience and that you have the right to submit any included media.",
          "You grant ESIELL a non-exclusive, worldwide, royalty-free permission to store, review, reproduce, format, translate, publish, display, and use the submitted review for operating, promoting, and improving ESIELL, subject to applicable law and our Privacy Policy.",
          "This permission does not transfer your ownership of original content to ESIELL.",
          "You may contact us to request correction or removal of a published review.",
        ],
      },
      {
        title: "16. Review and Content Rules",
        paragraphs: [
          "You must not submit content that:",
        ],
        bullets: [
          "Is knowingly false, misleading, fraudulent, or unrelated to the product or service.",
          "Contains harassment, threats, hate speech, unlawful discrimination, or obscene material.",
          "Infringes copyright, trademark, privacy, publicity, or another person’s rights.",
          "Contains malware, harmful links, spam, advertisements, or unauthorized promotions.",
          "Includes another person’s private information without authorization.",
          "Contains payment-card details, passwords, private account credentials, or other sensitive information.",
          "Violates applicable law or encourages unlawful activity.",
        ],
      },
      {
        title: "17. Content Moderation",
        paragraphs: [
          "We may review, delay, reject, edit for formatting, hide, or remove submitted content where reasonably necessary for safety, relevance, privacy, legal compliance, quality, or enforcement of these Terms.",
          "We are not required to publish every submitted review or explain every moderation decision.",
          "We will not intentionally change the substantive meaning of a review when making formatting, spelling, or privacy-related edits.",
        ],
      },
      {
        title: "18. Website Use",
        paragraphs: [
          "You may use the website only for lawful personal or commercial shopping purposes consistent with these Terms.",
          "You must not:",
        ],
        bullets: [
          "Attempt to access another person’s account or restricted administrative systems.",
          "Interfere with the website, servers, security, availability, or normal operation.",
          "Introduce viruses, malicious code, automated attacks, or harmful material.",
          "Scrape, copy, harvest, or extract website data at scale without written permission.",
          "Use bots or automated tools to abuse checkout, inventory, promotions, reviews, or accounts.",
          "Reverse engineer or attempt to discover protected source code except where such restriction is prohibited by law.",
          "Use ESIELL content, branding, or services for fraud, impersonation, or misleading activity.",
        ],
      },
      {
        title: "19. Intellectual Property",
        paragraphs: [
          "The website and its branding, logos, layout, graphics, photographs, videos, product presentation, written content, software, and other materials are owned by ESIELL or used with appropriate permission.",
          "You may view and use the website for its intended purpose. You may not reproduce, distribute, sell, modify, republish, or commercially exploit protected materials without permission, except where applicable law expressly permits it.",
          "Third-party trademarks and content remain the property of their respective owners.",
        ],
      },
      {
        title: "20. Third-Party Services",
        paragraphs: [
          "The website may connect to payment providers, delivery companies, social networks, WhatsApp, Meta services, maps, analytics tools, or external websites.",
          "Third-party services are governed by their own terms and policies. ESIELL is not responsible for a third party’s independent service, availability, content, security, or processing practices.",
        ],
      },
      {
        title: "21. Privacy",
        paragraphs: [
          "Our collection and use of personal information is described in our Privacy Policy, which forms part of these Terms.",
        ],
        links: [
          {
            label: "Read the Privacy Policy",
            to: "/privacy-policy",
          },
        ],
      },
      {
        title: "22. Service Availability",
        paragraphs: [
          "We may update, maintain, suspend, restrict, replace, or discontinue any website feature where reasonably necessary.",
          "We do not guarantee that the website will always be uninterrupted, error-free, secure, or compatible with every device or browser.",
        ],
      },
      {
        title: "23. Disclaimers",
        paragraphs: [
          "To the extent permitted by law, the website and its digital features are provided on an “as available” basis.",
          "We do not guarantee that every product will remain available, every visual representation will be exact, or every delivery estimate will be achieved.",
          "Nothing in these Terms limits obligations or warranties that cannot be excluded under applicable law.",
        ],
      },
      {
        title: "24. Limitation of Liability",
        paragraphs: [
          "To the maximum extent permitted by applicable law, ESIELL will not be responsible for indirect, incidental, special, or consequential loss arising from the use of the website, delay, third-party service, or an event outside our reasonable control.",
          "Where liability cannot be excluded, it will be limited only to the extent permitted by applicable law.",
          "Nothing in these Terms excludes liability that cannot legally be excluded.",
        ],
      },
      {
        title: "25. Indemnity for Misuse",
        paragraphs: [
          "To the extent permitted by law, you may be responsible for losses, claims, or reasonable costs caused by your unlawful use of the website, infringement of another person’s rights, fraudulent activity, or material breach of these Terms.",
        ],
      },
      {
        title: "26. Force Majeure",
        paragraphs: [
          "ESIELL is not responsible for delay or failure caused by circumstances outside its reasonable control, including natural events, severe weather, interruption of utilities or communications, courier disruption, strikes, public emergencies, governmental action, conflict, security events, or failures of third-party infrastructure.",
        ],
      },
      {
        title: "27. Suspension and Termination",
        paragraphs: [
          "We may suspend or terminate access where reasonably necessary because of unlawful conduct, fraud, abuse, security risk, repeated failed deliveries, violation of these Terms, or harm to ESIELL, customers, or third parties.",
          "Provisions that reasonably need to continue after termination, including payment obligations, intellectual-property rules, liability provisions, and dispute terms, will remain effective.",
        ],
      },
      {
        title: "28. Governing Law and Disputes",
        paragraphs: [
          "These Terms are governed by the applicable laws of the Lebanese Republic, without excluding any mandatory consumer protection that applies to the transaction.",
          "We encourage customers to contact ESIELL first so that we can attempt to resolve any concern amicably.",
          "Where a dispute cannot be resolved amicably, it may be submitted to the competent courts according to applicable Lebanese law.",
        ],
      },
      {
        title: "29. Changes to These Terms",
        paragraphs: [
          "We may update these Terms to reflect changes to our services, website, business operations, providers, or legal requirements.",
          "The updated version becomes effective when published on this page unless a later date is stated.",
          "Your continued use of the website after an update means that the updated Terms apply to future use and transactions.",
        ],
      },
      {
        title: "30. General Provisions",
        paragraphs: [
          "If any provision is found invalid or unenforceable, the remaining provisions will continue to apply.",
          "A failure to enforce a provision immediately does not waive the right to enforce it later.",
          "These Terms, the Privacy Policy, and any specific terms displayed for an order or promotion form the agreement relating to your use of the relevant service.",
        ],
      },
      {
        title: "31. Contact Us",
        paragraphs: [
          "For questions about these Terms, orders, exchanges, returns, delivery, reviews, or the website, contact ESIELL through our Contact page or WhatsApp customer service.",
        ],
        links: [
          {
            label: "Open the Contact page",
            to: "/contact",
          },
          {
            label: "Contact ESIELL on WhatsApp",
            href: "https://wa.me/96179338100",
          },
        ],
      },
    ],
  },

  AR: {
    title: "الشروط والأحكام",
    intro:
      "تحكم هذه الشروط استخدامك لموقع esiell.com وشراء المنتجات أو استخدام الخدمات التي تقدمها إيسيل. باستخدام الموقع أو تقديم طلب، فإنك توافق على هذه الشروط.",
    sections: [
      {
        title: "1. حول إيسيل",
        paragraphs: [
          "إيسيل هي جهة متخصصة في بيع الأحذية ومنتجات الموضة في لبنان. تنطبق هذه الشروط على موقع إيسيل وحسابات الزبائن والطلبات وخدمة الزبائن والمراجعات والعروض والخدمات المرتبطة بها.",
          "يُقصد بعبارات «إيسيل» و«نحن» و«لنا» إيسيل والأشخاص المسؤولون عن تشغيل الموقع وتقديم خدماته.",
        ],
      },
      {
        title: "2. الموافقة على الشروط",
        paragraphs: [
          "عند استخدام الموقع أو إنشاء حساب أو تقديم طلب أو إرسال محتوى أو استخدام أي خدمة من إيسيل، فإنك تؤكد أنك قرأت هذه الشروط وسياسة الخصوصية ووافقت عليهما.",
          "إذا لم توافق على هذه الشروط، يجب عدم استخدام الموقع أو تقديم طلب.",
        ],
      },
      {
        title: "3. الأهلية",
        paragraphs: [
          "يجب أن تكون لديك الأهلية القانونية لإبرام معاملة شراء وفقاً للقانون المطبق.",
          "يؤكد الشخص الذي يستخدم الموقع نيابة عن شخص أو شركة أو مؤسسة أنه مخول بالتصرف نيابة عنها.",
        ],
      },
      {
        title: "4. حسابات الزبائن",
        bullets: [
          "يجب تقديم معلومات صحيحة وحديثة وكاملة.",
          "أنت مسؤول عن الحفاظ على سرية كلمة المرور وبيانات الدخول.",
          "أنت مسؤول عن النشاط الذي يتم عبر حسابك ما لم تبلغنا فوراً عن دخول غير مصرح به.",
          "يُمنع انتحال شخصية شخص آخر أو إنشاء حسابات احتيالية أو استخدام الموقع لأغراض غير قانونية.",
          "يجوز لنا تعليق الحساب أو تقييده عند الحاجة المعقولة لحماية الزبائن أو إيسيل أو الموقع.",
        ],
      },
      {
        title: "5. معلومات المنتجات",
        paragraphs: [
          "نسعى إلى عرض أسماء المنتجات وأوصافها وألوانها وموادها وصورها ومقاساتها وأسعارها وتوفرها بأكبر قدر معقول من الدقة.",
          "قد تظهر الألوان والتفاصيل بشكل مختلف بحسب الإضاءة والتصوير وإعدادات الشاشة والجهاز المستخدم.",
          "قد توجد اختلافات بسيطة في التغليف أو التشطيب أو الملمس أو الشكل، ولا يعني ذلك بالضرورة أن المنتج معيب.",
          "لا يُضمن توفر المنتج بصورة نهائية إلا بعد مراجعة الطلب وتأكيده.",
        ],
      },
      {
        title: "6. اختيار المقاس",
        paragraphs: [
          "يتحمل الزبون مسؤولية اختيار المقاس المفضل. ويتم تقديم إرشادات المقاس للمساعدة، لكنها لا تضمن تطابق القياس لكل شخص أو تصميم.",
          "يمكنك التواصل مع إيسيل قبل الطلب للحصول على مساعدة في اختيار المقاس.",
        ],
      },
      {
        title: "7. الأسعار",
        paragraphs: [
          "تُعرض الأسعار بالعملة المبينة على الموقع، وقد تتغير دون إشعار مسبق.",
          "يكون السعر المطبق عادة هو السعر المعروض عند تقديم الطلب، مع حقنا في تصحيح الأخطاء الواضحة.",
          "قد تظهر رسوم التوصيل والرسوم الإضافية والخصومات والتعديلات الترويجية بصورة منفصلة عند الدفع أو تأكيد الطلب.",
          "إذا كان السعر خاطئاً بوضوح بسبب خطأ تقني أو إداري أو مطبعي، يمكننا التواصل معك لتصحيح الطلب أو إلغائه.",
        ],
      },
      {
        title: "8. تقديم الطلب",
        paragraphs: [
          "يُعد تقديم الطلب عرضاً لشراء المنتجات المختارة. ويعني التأكيد الآلي أو الرسالة الظاهرة على الشاشة أننا استلمنا الطلب، لكنه لا يعني بالضرورة القبول النهائي.",
          "يُعتبر الطلب مقبولاً عندما تؤكده إيسيل أو تبدأ بتجهيزه أو ترسله أو تبلغك بقبوله.",
          "قد نتواصل معك للتحقق من الطلب أو العنوان أو رقم الهاتف أو التوفر أو وسيلة الدفع أو أي معلومات أخرى.",
        ],
      },
      {
        title: "9. رفض الطلب أو إلغاؤه",
        paragraphs: [
          "يجوز لنا رفض الطلب أو تحديد كميته أو تعليقه أو إلغاؤه لأسباب معقولة، ومنها عدم توفر المنتج أو خطأ السعر أو الاشتباه بالاحتيال أو نقص المعلومات أو فشل التحقق أو قيود التوصيل أو إساءة الاستخدام أو الخطأ التقني.",
          "سنحاول إعلامك باستخدام معلومات الاتصال المقدمة متى كان ذلك ممكناً بشكل معقول.",
          "إذا ألغينا طلباً بعد استلام الدفع، فسيتم التعامل مع أي مبلغ مستحق للإعادة بالطريقة المناسبة، مع مراعاة أوقات معالجة مزود الدفع.",
        ],
      },
      {
        title: "10. الدفع",
        paragraphs: [
          "قد تختلف وسائل الدفع المتاحة بحسب الطلب أو المنطقة أو طريقة التوصيل أو إعدادات الموقع.",
          "أنت تؤكد أنك مخول باستخدام وسيلة الدفع المقدمة.",
          "قد تخضع عمليات الدفع التي يعالجها مزود خارجي لشروط وسياسات ذلك المزود.",
          "يجوز لإيسيل طلب تأكيد الدفع أو تحقق إضافي قبل قبول الطلب أو إرساله.",
        ],
      },
      {
        title: "11. التوصيل والاستلام",
        paragraphs: [
          "يتم تقديم أوقات التوصيل التقديرية بحسن نية، لكنها ليست مضمونة إلا إذا تم النص على ذلك صراحة.",
          "قد يتأثر التوصيل بعمل شركة الشحن أو الطقس أو حركة السير أو دقة العنوان أو التوفر أو العطل الرسمية أو الظروف الأمنية أو القوة القاهرة أو الأحداث الخارجة عن سيطرتنا المعقولة.",
          "أنت مسؤول عن تقديم عنوان كامل وصحيح ورقم هاتف يمكن الوصول إليه.",
          "قد يُطلب منك أو من شخص مخول فحص الطلب واستلامه عند التوصيل أو الاستلام.",
          "قد تترتب رسوم إضافية على محاولات التوصيل الإضافية أو تعديل العنوان حيث يكون ذلك مناسباً.",
        ],
      },
      {
        title: "12. الفحص والاستبدال والإرجاع واسترداد الأموال",
        paragraphs: [
          "يجب فحص المنتجات بعد استلامها والتواصل معنا بأقرب وقت معقول إذا كان المنتج مفقوداً أو متضرراً أو معيباً أو مختلفاً بشكل جوهري عن الطلب المؤكد.",
          "قد تعتمد أهلية الاستبدال أو الإرجاع أو استرداد المبلغ على حالة المنتج وسبب الطلب ومتطلبات النظافة والاستعمال والتغليف والتوقيت والتوفر والقانون المطبق.",
          "يجب إبقاء المنتج غير مستخدم وغير ملبوس وغير متضرر ونظيفاً وفي حالته وتغليفه الأصليين أثناء مراجعة طلب الإرجاع أو الاستبدال.",
          "لا ترسلي المنتج قبل التواصل مع إيسيل والحصول على التعليمات.",
          "سيتم توضيح أي رسوم توصيل أو استبدال أو إرجاع بحسب ظروف الطلب.",
          "لا تستبعد هذه الشروط أي حق إلزامي للزبون لا يسمح القانون باستبعاده.",
        ],
      },
      {
        title: "13. العروض ورموز الخصم",
        paragraphs: [
          "قد تخضع العروض ورموز الخصم والتوصيل المجاني والهدايا وأسعار التنزيلات لشروط أهلية وتواريخ وكميات ومنتجات مستثناة وحد أدنى وحدود جغرافية وقيود استخدام.",
          "ما لم يُذكر خلاف ذلك، لا يمكن جمع العروض أو استبدالها نقداً أو نقلها أو تطبيقها بأثر رجعي.",
          "يجوز لنا رفض أو إلغاء إساءة استخدام العروض، بما يشمل الاستخدام المتكرر أو الآلي أو الاحتيالي أو غير المصرح به.",
        ],
      },
      {
        title: "14. التواصل مع الزبائن",
        paragraphs: [
          "أنت توافق على أن نتواصل معك عبر البريد الإلكتروني أو رقم الهاتف أو رسائل الموقع أو رقم واتساب المرتبط بحسابك أو طلبك لأغراض تشغيلية.",
          "قد تشمل الرسائل التشغيلية تأكيد الطلب والتحقق والتوفر والتوصيل والدفع والإرجاع والدعم والأمان وطلبات المراجعة.",
          "تخضع الرسائل التسويقية لسياسة الخصوصية وخيارات الموافقة أو إلغاء الاشتراك.",
        ],
      },
      {
        title: "15. مراجعات العملاء",
        paragraphs: [
          "قد تتم دعوة الزبائن لتقديم مراجعة بنص أو تقييم أو صورة أو فيديو أو ملاحظة صوتية أو وسيلة أخرى مدعومة.",
          "عند تقديم مراجعة بصورة اختيارية، فإنك تؤكد أنها تعكس تجربتك الحقيقية وأن لديك الحق في تقديم الوسائط المرفقة.",
          "تمنح إيسيل ترخيصاً غير حصري وعالمياً ودون رسوم لحفظ المراجعة ومراجعتها ونسخها وتنسيقها وترجمتها ونشرها وعرضها واستخدامها لتشغيل إيسيل والترويج لها وتحسينها، وفقاً للقانون وسياسة الخصوصية.",
          "لا ينقل هذا الترخيص ملكيتك للمحتوى الأصلي إلى إيسيل.",
          "يمكنك التواصل معنا لطلب تصحيح أو إزالة مراجعة منشورة.",
        ],
      },
      {
        title: "16. قواعد المراجعات والمحتوى",
        paragraphs: ["يُمنع إرسال محتوى:"],
        bullets: [
          "كاذب عن علم أو مضلل أو احتيالي أو غير مرتبط بالمنتج أو الخدمة.",
          "يتضمن مضايقة أو تهديداً أو خطاب كراهية أو تمييزاً غير قانوني أو محتوى فاحشاً.",
          "ينتهك حقوق النشر أو العلامات التجارية أو الخصوصية أو أي حق لشخص آخر.",
          "يتضمن برمجيات ضارة أو روابط مؤذية أو رسائل مزعجة أو إعلانات غير مصرح بها.",
          "يتضمن معلومات خاصة بشخص آخر دون إذنه.",
          "يتضمن بيانات بطاقات أو كلمات مرور أو بيانات دخول أو معلومات حساسة.",
          "يخالف القانون أو يشجع على نشاط غير قانوني.",
        ],
      },
      {
        title: "17. الإشراف على المحتوى",
        paragraphs: [
          "يجوز لنا مراجعة المحتوى أو تأخير نشره أو رفضه أو تنسيقه أو إخفاؤه أو حذفه عند الحاجة المعقولة للأمان أو الصلة أو الخصوصية أو الالتزام القانوني أو الجودة أو تطبيق الشروط.",
          "لسنا ملزمين بنشر كل مراجعة أو بشرح كل قرار إشراف.",
          "لن نغير عن قصد المعنى الأساسي للمراجعة عند إجراء تعديلات تنسيقية أو إملائية أو متعلقة بالخصوصية.",
        ],
      },
      {
        title: "18. استخدام الموقع",
        paragraphs: [
          "يمكن استخدام الموقع فقط لأغراض التسوق المشروعة والمتوافقة مع هذه الشروط.",
          "يُمنع:",
        ],
        bullets: [
          "محاولة الدخول إلى حساب شخص آخر أو أنظمة الإدارة المقيدة.",
          "التدخل في الموقع أو الخوادم أو الأمان أو التوفر أو التشغيل الطبيعي.",
          "إدخال فيروسات أو أكواد ضارة أو هجمات آلية أو مواد مؤذية.",
          "جمع أو نسخ أو استخراج بيانات الموقع على نطاق واسع دون إذن خطي.",
          "استخدام برامج آلية لإساءة استخدام الدفع أو المخزون أو العروض أو المراجعات أو الحسابات.",
          "الهندسة العكسية أو محاولة اكتشاف كود محمي، إلا حيث يمنع القانون هذا القيد.",
          "استخدام محتوى إيسيل أو علامتها أو خدماتها للاحتيال أو الانتحال أو التضليل.",
        ],
      },
      {
        title: "19. الملكية الفكرية",
        paragraphs: [
          "الموقع وعلامته وشعاراته وتصميمه ورسوماته وصوره وفيديوهاته وطريقة عرض المنتجات ونصوصه وبرمجياته ومواده مملوكة لإيسيل أو مستخدمة بإذن مناسب.",
          "يمكنك استخدام الموقع للغرض المخصص له، ولا يجوز نسخ المواد المحمية أو توزيعها أو بيعها أو تعديلها أو إعادة نشرها أو استغلالها تجارياً دون إذن، إلا حيث يسمح القانون بذلك صراحة.",
          "تبقى علامات ومحتوى الجهات الأخرى ملكاً لأصحابها.",
        ],
      },
      {
        title: "20. خدمات الجهات الأخرى",
        paragraphs: [
          "قد يرتبط الموقع بمزودي الدفع وشركات التوصيل وشبكات التواصل وواتساب وخدمات Meta والخرائط وأدوات التحليل والمواقع الخارجية.",
          "تخضع هذه الخدمات لشروطها وسياساتها، ولا تتحمل إيسيل مسؤولية الخدمة المستقلة أو التوفر أو المحتوى أو الأمان أو ممارسات المعالجة الخاصة بجهة أخرى.",
        ],
      },
      {
        title: "21. الخصوصية",
        paragraphs: [
          "توضح سياسة الخصوصية الخاصة بنا كيفية جمع المعلومات الشخصية واستخدامها، وهي تشكل جزءاً من هذه الشروط.",
        ],
        links: [
          {
            label: "قراءة سياسة الخصوصية",
            to: "/privacy-policy",
          },
        ],
      },
      {
        title: "22. توفر الخدمة",
        paragraphs: [
          "يجوز لنا تحديث أو صيانة أو تعليق أو تقييد أو استبدال أو إيقاف أي ميزة عند الحاجة المعقولة.",
          "لا نضمن أن الموقع سيكون متاحاً دائماً دون انقطاع أو أخطاء أو أنه سيتوافق مع كل جهاز أو متصفح.",
        ],
      },
      {
        title: "23. إخلاء المسؤولية",
        paragraphs: [
          "في حدود ما يسمح به القانون، يتم توفير الموقع وميزاته الرقمية بحسب توفرها.",
          "لا نضمن بقاء كل منتج متوفراً أو أن يكون التمثيل البصري مطابقاً تماماً أو تحقيق كل وقت توصيل تقديري.",
          "لا تحد هذه الشروط من أي التزام أو ضمان لا يسمح القانون باستبعاده.",
        ],
      },
      {
        title: "24. تحديد المسؤولية",
        paragraphs: [
          "إلى أقصى حد يسمح به القانون، لا تتحمل إيسيل الخسائر غير المباشرة أو العرضية أو الخاصة أو التبعية الناتجة عن استخدام الموقع أو التأخير أو خدمة جهة أخرى أو حدث خارج السيطرة المعقولة.",
          "عندما لا يمكن استبعاد المسؤولية، تكون محدودة فقط بالقدر الذي يسمح به القانون.",
          "لا تستبعد هذه الشروط أي مسؤولية لا يسمح القانون باستبعادها.",
        ],
      },
      {
        title: "25. المسؤولية عن إساءة الاستخدام",
        paragraphs: [
          "في حدود ما يسمح به القانون، قد تتحمل مسؤولية الخسائر أو المطالبات أو التكاليف المعقولة الناتجة عن استخدامك غير القانوني للموقع أو انتهاكك حقوق شخص آخر أو النشاط الاحتيالي أو المخالفة الجوهرية لهذه الشروط.",
        ],
      },
      {
        title: "26. القوة القاهرة",
        paragraphs: [
          "لا تتحمل إيسيل مسؤولية التأخير أو عدم التنفيذ الناتج عن ظروف خارجة عن سيطرتها المعقولة، بما يشمل الكوارث الطبيعية والطقس القاسي وانقطاع الخدمات أو الاتصالات وتعطل شركات التوصيل والإضرابات والطوارئ والإجراءات الحكومية والنزاعات والأحداث الأمنية وتعطل البنية التحتية الخارجية.",
        ],
      },
      {
        title: "27. تعليق الخدمة وإنهاؤها",
        paragraphs: [
          "يجوز لنا تعليق أو إنهاء الوصول عند الحاجة المعقولة بسبب سلوك غير قانوني أو احتيال أو إساءة استخدام أو خطر أمني أو تكرار فشل التوصيل أو مخالفة الشروط أو الإضرار بإيسيل أو الزبائن أو الغير.",
          "تبقى الأحكام التي يلزم استمرارها بعد الإنهاء سارية، بما في ذلك التزامات الدفع والملكية الفكرية والمسؤولية والنزاعات.",
        ],
      },
      {
        title: "28. القانون والنزاعات",
        paragraphs: [
          "تخضع هذه الشروط للقوانين المطبقة في الجمهورية اللبنانية، دون استبعاد أي حماية إلزامية للمستهلك تنطبق على المعاملة.",
          "نشجع الزبائن على التواصل مع إيسيل أولاً لمحاولة حل أي مشكلة بصورة ودية.",
          "إذا تعذر الحل الودي، يمكن إحالة النزاع إلى المحاكم المختصة وفقاً للقانون اللبناني.",
        ],
      },
      {
        title: "29. تعديل الشروط",
        paragraphs: [
          "يجوز لنا تحديث هذه الشروط لتعكس التغييرات في خدماتنا أو الموقع أو العمليات أو مزودي الخدمات أو المتطلبات القانونية.",
          "تصبح النسخة المحدثة نافذة عند نشرها في هذه الصفحة ما لم يذكر تاريخ لاحق.",
          "يعني استمرار استخدامك للموقع بعد التحديث أن الشروط المحدثة ستطبق على الاستخدام والمعاملات المستقبلية.",
        ],
      },
      {
        title: "30. أحكام عامة",
        paragraphs: [
          "إذا تبين أن أحد الأحكام غير صالح أو غير قابل للتنفيذ، تبقى الأحكام الأخرى سارية.",
          "عدم تطبيق حكم فوراً لا يعني التنازل عن حق تطبيقه لاحقاً.",
          "تشكل هذه الشروط وسياسة الخصوصية وأي شروط خاصة تظهر للطلب أو العرض الاتفاق المتعلق باستخدام الخدمة.",
        ],
      },
      {
        title: "31. التواصل معنا",
        paragraphs: [
          "للاستفسار عن هذه الشروط أو الطلبات أو الاستبدال أو الإرجاع أو التوصيل أو المراجعات أو الموقع، تواصلي مع إيسيل عبر صفحة التواصل أو واتساب.",
        ],
        links: [
          {
            label: "فتح صفحة التواصل",
            to: "/contact",
          },
          {
            label: "التواصل مع إيسيل عبر واتساب",
            href: "https://wa.me/96179338100",
          },
        ],
      },
    ],
  },
};

function LegalPage({ type }) {
  const { lang } = useContext(LanguageContext);
  const location = useLocation();

  const normalizedLang = lang === "AR" ? "AR" : "EN";

  const content =
    type === "privacy"
      ? PRIVACY_CONTENT[normalizedLang]
      : TERMS_CONTENT[normalizedLang];

  const lastUpdated =
    normalizedLang === "AR" ? LAST_UPDATED_AR : LAST_UPDATED_EN;

  useEffect(() => {
    window.scrollTo({
      top: 0,
      behavior: "instant",
    });
  }, [location.pathname]);

  useEffect(() => {
    const pageName =
      type === "privacy"
        ? normalizedLang === "AR"
          ? "سياسة الخصوصية"
          : "Privacy Policy"
        : normalizedLang === "AR"
        ? "الشروط والأحكام"
        : "Terms of Service";

    document.title = `${pageName} | ESIELL`;

    let description = document.querySelector(
      'meta[name="description"]'
    );

    if (!description) {
      description = document.createElement("meta");
      description.setAttribute("name", "description");
      document.head.appendChild(description);
    }

    description.setAttribute(
      "content",
      type === "privacy"
        ? normalizedLang === "AR"
          ? "سياسة خصوصية إيسيل وكيفية جمع المعلومات الشخصية واستخدامها وحمايتها وحذفها."
          : "ESIELL Privacy Policy explaining how personal information is collected, used, protected, shared, retained, and deleted."
        : normalizedLang === "AR"
        ? "الشروط والأحكام التي تنظم استخدام موقع إيسيل والطلبات والتوصيل والمراجعات والخدمات."
        : "ESIELL Terms of Service governing website use, orders, delivery, customer reviews, and related services."
    );
  }, [type, normalizedLang]);

  return (
    <div
      dir={normalizedLang === "AR" ? "rtl" : "ltr"}
      className="min-h-screen bg-white text-[#2E3135]"
    >
      <TopBanner />

      <MainHeader
        onMenuOpen={() => {}}
        onSearchOpen={() => {}}
        onBagOpen={() => {}}
      />

      <CartDrawer />
      <AddedToBagToast />

      <main className="px-5 py-10 sm:px-8 md:py-14 lg:px-16 xl:px-24">
        <article className="mx-auto max-w-5xl">
          <div className="mb-10 border-b border-[#E6D8D2] pb-8 text-center">
            <p className="mb-3 text-xs font-semibold uppercase tracking-[0.24em] text-[#8B7D78]">
              ESIELL
            </p>

            <h1 className="text-3xl font-semibold tracking-tight text-[#2E3135] sm:text-4xl lg:text-5xl">
              {content.title}
            </h1>

            <p className="mt-4 text-sm text-[#746A67]">
              {normalizedLang === "AR"
                ? `آخر تحديث: ${lastUpdated}`
                : `Last updated: ${lastUpdated}`}
            </p>
          </div>

          <div className="mb-10 rounded-2xl border border-[#E6D8D2] bg-[#F7F3F1] p-5 sm:p-7">
            <p className="text-sm leading-7 text-[#514846] sm:text-base">
              {content.intro}
            </p>
          </div>

          <div className="space-y-10">
            {content.sections.map((section) => (
              <section
                key={section.title}
                className="scroll-mt-28 border-b border-[#EEE5E1] pb-9 last:border-b-0"
              >
                <h2 className="mb-4 text-xl font-semibold text-[#2E3135] sm:text-2xl">
                  {section.title}
                </h2>

                {section.paragraphs?.map((paragraph, index) => (
                  <p
                    key={`${section.title}-paragraph-${index}`}
                    className="mb-4 text-sm leading-7 text-[#514846] last:mb-0 sm:text-base sm:leading-8"
                  >
                    {paragraph}
                  </p>
                ))}

                {section.bullets?.length > 0 && (
                  <ul
                    className={`mt-4 space-y-3 text-sm leading-7 text-[#514846] sm:text-base sm:leading-8 ${
                      normalizedLang === "AR"
                        ? "list-disc pr-6"
                        : "list-disc pl-6"
                    }`}
                  >
                    {section.bullets.map((bullet, index) => (
                      <li key={`${section.title}-bullet-${index}`}>
                        {bullet}
                      </li>
                    ))}
                  </ul>
                )}

                {section.links?.length > 0 && (
                  <div className="mt-6 flex flex-col gap-3 sm:flex-row sm:flex-wrap">
                    {section.links.map((link) =>
                      link.to ? (
                        <Link
                          key={link.label}
                          to={link.to}
                          className="inline-flex min-h-[44px] items-center justify-center rounded-lg bg-black px-5 py-3 text-sm font-semibold text-white transition hover:bg-[#262626]"
                        >
                          {link.label}
                        </Link>
                      ) : (
                        <a
                          key={link.label}
                          href={link.href}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex min-h-[44px] items-center justify-center rounded-lg border border-[#D8CAC4] bg-white px-5 py-3 text-sm font-semibold text-[#2E3135] transition hover:bg-[#F7F3F1]"
                        >
                          {link.label}
                        </a>
                      )
                    )}
                  </div>
                )}
              </section>
            ))}
          </div>

          <div className="mt-12 rounded-2xl border border-[#E6D8D2] bg-[#FFF9F6] p-6 text-center sm:p-8">
            <h2 className="text-xl font-semibold text-[#2E3135]">
              {normalizedLang === "AR"
                ? "هل لديك أي استفسار؟"
                : "Have a question?"}
            </h2>

            <p className="mx-auto mt-3 max-w-2xl text-sm leading-7 text-[#665B58]">
              {normalizedLang === "AR"
                ? "يمكنك التواصل مع فريق إيسيل لطلب المساعدة أو الاستفسار عن معلوماتك أو حسابك أو طلباتك."
                : "Contact the ESIELL team for help with your information, account, orders, or use of our services."}
            </p>

            <Link
              to="/contact"
              className="mt-5 inline-flex min-h-[44px] items-center justify-center rounded-lg bg-black px-6 py-3 text-sm font-semibold text-white transition hover:bg-[#262626]"
            >
              {normalizedLang === "AR"
                ? "تواصلي معنا"
                : "Contact Us"}
            </Link>
          </div>
        </article>
      </main>

      <Footer />
    </div>
  );
}

export default LegalPage;
